import { motion } from "framer-motion";
import { MapPin } from "lucide-react";
import React from "react";

interface AddressFormProps {
  formData: any;
  addressData: any;
  onAddressChange: (field: string, value: string) => void;
  onInputChange: (field: string, value: any) => void;
}

const AddressForm: React.FC<AddressFormProps> = ({
  formData,
  addressData,
  onAddressChange,
  onInputChange,
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="text-center">
        <h3 className="text-lg font-medium text-gray-900">
          Address Information
        </h3>
        <p className="text-sm text-gray-500 mt-1">Provide location details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Region
          </label>
          <select
            value={formData.region}
            onChange={(e) => onAddressChange("region", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            required
          >
            <option value="">Select Region</option>
            {addressData.regions.map((region: any) => (
              <option key={region.id} value={region.id}>
                {region.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            District
          </label>
          <select
            value={formData.district}
            onChange={(e) => onAddressChange("district", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            disabled={!formData.region}
            required
          >
            <option value="">Select District</option>
            {addressData.districts.map((district: any) => (
              <option key={district.id} value={district.id}>
                {district.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            County
          </label>
          <select
            value={formData.county}
            onChange={(e) => onAddressChange("county", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            disabled={!formData.district}
            required
          >
            <option value="">Select County</option>
            {addressData.counties.map((county: any) => (
              <option key={county.id} value={county.id}>
                {county.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Sub County
          </label>
          <select
            value={formData.subCounty}
            onChange={(e) => onAddressChange("subCounty", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            disabled={!formData.county}
            required
          >
            <option value="">Select Sub County</option>
            {addressData.subCounties.map((subCounty: any) => (
              <option key={subCounty.id} value={subCounty.id}>
                {subCounty.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Parish
          </label>
          <select
            value={formData.parish}
            onChange={(e) => onInputChange("parish", e.target.value)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            disabled={!formData.subCounty}
            required
          >
            <option value="">Select Parish</option>
            {addressData.parishes.map((parish: any) => (
              <option key={parish.id} value={parish.id}>
                {parish.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            Street Address
          </label>
          <div className="mt-1 relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={formData.streetAddress}
              onChange={(e) => onInputChange("streetAddress", e.target.value)}
              className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
              placeholder="Street, building, apartment"
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AddressForm;
